import { FormErrors } from '../types';

// Form validation helpers
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePassword = (password: string): boolean => {
  // 8 chars is a pilot-only floor. NIST SP 800-63B-4 wants 15 for
  // single-factor or 8 paired with MFA — before broader release, move to
  // either posture. Mirrored in the Supabase Dashboard Auth Policies; keep
  // them in sync.
  return password.length >= 8;
};

export const validateForm = (fields: Record<string, any>, rules: Record<string, (value: any) => string | null>): FormErrors => {
  const errors: FormErrors = {};
  
  Object.keys(rules).forEach(field => {
    const value = fields[field];
    const error = rules[field](value);
    if (error) {
      errors[field] = error;
    }
  });
  
  return errors;
};

// Date and time helpers
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const timeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInDays > 0) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  } else if (diffInHours > 0) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  } else {
    return 'Just now';
  }
};

// File helpers
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getFileExtension = (filename: string): string => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

export const isValidFileType = (file: File, allowedTypes: string[]): boolean => {
  return allowedTypes.includes(file.type);
};

export const isValidFileSize = (file: File, maxSize: number): boolean => {
  return file.size <= maxSize;
};

// String helpers
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const capitalizeFirst = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Status helpers
export const getStatusColor = (status: string): string => {
  // phase-2b: 'reviewing' kept literal orange — activity state, pending dedicated token
  const statusColors: Record<string, string> = {
    'uploading': 'text-info bg-info-soft',
    'configured': 'text-warning bg-warning-soft',
    'analyzing': 'text-ai bg-ai-soft',
    'reviewing': 'text-orange-600 bg-orange-100',
    'completed': 'text-success bg-success-soft',
    'failed': 'text-danger bg-danger-soft',
    'pending': 'text-gray-600 bg-gray-100',
    'processing': 'text-info bg-info-soft',
  };

  return statusColors[status] || 'text-gray-600 bg-gray-100';
};

export const getRiskLevelColor = (level: string): string => {
  const riskColors: Record<string, string> = {
    'high': 'text-danger bg-danger-soft border-danger-soft',
    'medium': 'text-warning bg-warning-soft border-warning-soft',
    'low': 'text-success bg-success-soft border-success-soft',
  };

  return riskColors[level] || 'text-gray-600 bg-gray-100 border-gray-200';
};

// Error handling helpers
export const getErrorMessage = (error: any): string => {
  // Import dynamically to avoid circular dependencies
  const { getEnhancedErrorMessage } = require('./errorHandling');
  return getEnhancedErrorMessage(error);
};

// Legacy function - kept for backward compatibility
export const getBasicErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.response?.data?.detail) return error.response.data.detail;
  return 'An unexpected error occurred';
};

// Local storage helpers
export const setLocalStorage = (key: string, value: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error setting localStorage:', error);
  }
};

export const getLocalStorage = (key: string): any => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Error getting localStorage:', error);
    return null;
  }
};

export const removeLocalStorage = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing localStorage:', error);
  }
};